import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const SuccessStories = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const testimonials = [
    {
      id: 1,
      title: "200+ Engineers Trained",
      description: "Mile LXP's enterprise solution helped us standardize technical training across our global workforce. The compliance tracking and certification management saved us $2M annually in training costs. Our teams now complete certifications 60% faster with better retention rates.",
      name: "David Park",
      role: "VP of Operations",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop"
    },
    {
      id: 2,
      title: "300% ROI on Training",
      description: "We needed to upskill our entire engineering team for cloud migration. Mile LXP's enterprise platform made it seamless to track progress across 200+ engineers. The custom learning paths and compliance reporting were game-changers.",
      name: "James Wilson",
      role: "VP of Operations",
      image: "https://images.unsplash.com/photo-1519085368-af11f7cbe7?w=100&h=100&fit=crop"
    },
    {
      id: 3,
      title: "50% Faster Onboarding",
      description: "Implementing Mile LXP transformed our onboarding process completely. New hires are now productive in half the time, and our HR team has reduced administrative work by 70%. The analytics dashboard gives us insights we never had before.",
      name: "Sarah Chen",
      role: "Chief Learning Officer",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop"
    },
    {
      id: 4,
      title: "95% Course Completion Rate",
      description: "Our engagement rates skyrocketed after switching to Mile LXP. The personalized learning paths and mobile-first design meant our field teams could learn anywhere. We've seen a 95% course completion rate across all departments.",
      name: "Michael Rodriguez",
      role: "Director of Training",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop"
    },
    {
      id: 5,
      title: "Unified Global Training",
      description: "With teams in 15 countries, we needed a solution that could scale. Mile LXP's multi-language support and regional compliance features brought consistency to our global training program while respecting local requirements.",
      name: "Emma Thompson",
      role: "Global HR Director",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <>
      <style>{`
        /* Root & Layout */
        .success-stories-container {
          min-height: 100vh;
          background: var(--color-soft-teal, #f1f9f9);
          padding: 100px 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        [data-theme="dark"] .success-stories-container {
          background: var(--color-primary-dark, #0f172a);
        }

        .success-stories-wrapper {
          max-width: 1400px;
          margin: 0 auto;
        }

        /* Header */
        .success-stories-header {
          text-align: center;
          margin-bottom: 80px;
        }
        .success-badge {
          display: inline-block;
          background: var(--color-golden-hour, #fbbf24);
          color: var(--color-primary, #1e293b);
          padding: 10px 28px;
          border-radius: 9999px;
          font-size: 15px;
          font-weight: 600;
          margin-bottom: 24px;
        }
        .success-title {
          font-size: 52px;
          font-weight: 800;
          color: #0f172a;
          margin: 20px 0;
        }
        [data-theme="dark"] .success-title {
          color: white;
        }
        .success-subtitle {
          font-size: 19px;
          color: #475569;
          max-width: 800px;
          margin: 0 auto;
          line-height: 1.7;
        }
        [data-theme="dark"] .success-subtitle {
          color: #94a3b8;
        }

        /* Carousel Container */
        .carousel-container {
          position: relative;
          overflow: hidden;
          padding: 40px 0;
        }

        .carousel-track {
          display: flex;
          transition: transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
          will-change: transform;
        }

        /* Desktop: 3 Cards (with center focus) */
        @media (min-width: 769px) {
          .carousel-track {
            transform: translateX(calc(50% - ${currentSlide * 520 + 260}px));
          }

          .testimonial-card {
            min-width: 460px;
            max-width: 460px;
            margin: 0 30px;
            background: white;
            border-radius: 24px;
            padding: 48px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.08);
            transition: all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
            flex-shrink: 0;
          }

          [data-theme="dark"] .testimonial-card {
            background: #1e293b;
          }

          .testimonial-card.active {
            transform: scale(1.08);
            opacity: 1;
            z-index: 10;
            box-shadow: 0 40px 80px rgba(0,0,0,0.25);
          }

          .testimonial-card.prev,
          .testimonial-card.next {
            transform: scale(0.88);
            opacity: 0.6;
            filter: blur(1px);
          }
        }

        /* Mobile: 1 Card Full Width */
        @media (max-width: 768px) {
          .carousel-track {
            transform: translateX(-${currentSlide * 100}%);
          }

          .testimonial-card {
            max-width: 100%;
            padding: 40px 5rem;
            background: white;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.12);
            flex-shrink: 0;
          }

          [data-theme="dark"] .testimonial-card {
            background: #1e293b;
          }

          /* Only show current card */
          .testimonial-card {
            opacity: 1;
            transform: none !important;
          }
        }

        /* Card Content */
        .quote-icon {
          font-size: 80px;
          font-weight: 900;
          color: #3b82f6;
          line-height: 0.8;
          opacity: 0.7;
        }
        .stars-container {
          display: flex;
          gap: 6px;
        }
        .star {
          color: #fbbf24;
          font-size: 26px;
        }
        .testimonial-title {
          font-size: 26px;
          font-weight: 700;
          color: #0f172a;
          margin: 24px 0 16px;
        }
        [data-theme="dark"] .testimonial-title {
          color: white;
        }
        .testimonial-description {
          font-size: 17px;
          line-height: 1.8;
          color: #475569;
          font-style: italic;
          margin-bottom: 36px;
        }
        [data-theme="dark"] .testimonial-description {
          color: #cbd5e1;
        }

        .testimonial-author {
          display: flex;
          align-items: center;
          gap: 20px;
        }
        .author-image {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid #e2e8f0;
        }
        .author-name {
          font-weight: 600;
          font-size: 18px;
          color: #0f172a;
        }
        [data-theme="dark"] .author-name {
          color: white;
        }
        .author-role {
          color: #64748b;
          font-size: 15px;
        }

        /* Navigation Buttons */
        .nav-button {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 20;
          background: white;
          border: none;
          width: 64px;
          height: 64px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 15px 35px rgba(0,0,0,0.15);
          cursor: pointer;
          transition: all 0.3s ease;
        }
        [data-theme="dark"] .nav-button {
          background: #1e293b;
        }
        .nav-button:hover {
          transform: translateY(-50%) scale(1.15);
          box-shadow: 0 20px 45px rgba(0,0,0,0.25);
        }
        .nav-button-left {
          left: 20px;
        }
        .nav-button-right {
          right: 20px;
        }
        .nav-icon {
          color: #3b82f6;
        }
        [data-theme="dark"] .nav-icon {
          color: #60a5fa;
        }

        /* Dots */
        .pagination-dots {
          display: flex;
          justify-content: center;
          gap: 14px;
          margin-top: 60px;
        }
        .pagination-dot {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #e2e8f0;
          transition: all 0.4s ease;
          cursor: pointer;
        }
        .pagination-dot.active {
          background: #3b82f6;
          transform: scale(1.5);
        }
        [data-theme="dark"] .pagination-dot.active {
          background: #60a5fa;
        }

        /* Mobile Adjustments */
        @media (max-width: 768px) {
          .success-stories-container {
            padding: 60px 15px;
          }
          .success-title {
            font-size: 36px;
          }
          .success-subtitle {
            font-size: 16px;
          }
          .quote-icon {
            font-size: 60px;
          }
          .testimonial-title {
            font-size: 22px;
          }
          .testimonial-description {
            font-size: 15px;
            margin-bottom: 28px;
          }
          .author-image {
            width: 56px;
            height: 56px;
          }
          .author-name {
            font-size: 16px;
          }
          .nav-button {
            width: 56px;
            height: 56px;
          }
          .nav-button svg {
            width: 28px;
            height: 28px;
          }
          .nav-button-left {
            left: 10px;
          }
          .nav-button-right {
            right: 10px;
          }
          .pagination-dots {
            margin-top: 40px;
            gap: 10px;
          }
          .pagination-dot {
            width: 10px;
            height: 10px;
          }
        }
              @media (max-width: 500px) {
           .testimonial-card {
            padding: 40px 3rem;
          
          }
      }
      `}</style>

      <div className="success-stories-container">
        <div className="success-stories-wrapper">
          {/* Header */}
          <div className="success-stories-header">
            <span className="success-badge">Success Stories</span>
            <h1 className="success-title">Enterprise Success, Measurable ROI</h1>
            <p className="success-subtitle">
              Join 500+ enterprise clients who've transformed their workforce with Mile LXP.<br />
              Here are just a few of their remarkable success stories.
            </p>
          </div>

          {/* Carousel */}
          <div className="carousel-container">
            <button
              onClick={prevSlide}
              className="nav-button nav-button-left"
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={36} className="nav-icon" />
            </button>

            <button
              onClick={nextSlide}
              className="nav-button nav-button-right"
              aria-label="Next testimonial"
            >
              <ChevronRight size={36} className="nav-icon" />
            </button>

            <div className="carousel-track">
              {testimonials.map((testimonial, index) => {
                const isActive = index === currentSlide;

                return (
                  <div
                    key={testimonial.id}
                    className={`testimonial-card ${isActive ? 'active' : index === (currentSlide - 1 + testimonials.length) % testimonials.length ? 'prev' : index === (currentSlide + 1) % testimonials.length ? 'next' : ''}`}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                      <div className="quote-icon">"</div>
                      <div className="stars-container">
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className="star">★</span>
                        ))}
                      </div>
                    </div>

                    <h3 className="testimonial-title">{testimonial.title}</h3>
                    <p className="testimonial-description">{testimonial.description}</p>

                    <div className="testimonial-author">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="author-image"
                      />
                      <div>
                        <div className="author-name">{testimonial.name}</div>
                        <div className="author-role">{testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Dots */}
            <div className="pagination-dots">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`pagination-dot ${currentSlide === index ? 'active' : ''}`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SuccessStories;